import {
  FilterOutlined,
  PlusOutlined,
  SearchOutlined,
  UpOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons';
import { LinkButton, ScopeButton, ToggleButton } from '@monorepo/shared-lib';
import {
  Badge,
  Button,
  Col,
  DatePicker,
  Divider,
  InputNumber,
  Popover,
  Row,
  Select,
  Space,
  Typography,
} from 'antd';
import { ScopeCheckbox } from 'components/Custom/checkbox';
import { ProviderIcon } from 'components/Custom/ProviderIcon';
import { RoundInput } from 'components/Custom/inputs';
import { FilterSelect, FormSelect } from 'components/Custom/selects';
import dayjs from 'dayjs';
import 'dayjs/plugin/quarterOfYear';
import { usePrevious } from 'hooks/usePrevious';
import * as _ from 'lodash';
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AccountType, DateOptions, SourceType } from 'store/api/enums';
import {
  DataLogStatus,
  VIESContactStatistic,
  ViesStatus,
  dashboardStatistic,
} from 'store/app/enums';
import { getCommonCountries } from 'store/common/selectors';
import { getReportFilters } from 'store/vat/selectors';
import styled, { createGlobalStyle } from 'styled-components';
import { getCommonLabelFallbackKey } from 'shared/utils/common-label-fallbacks';
import { getProviderName } from 'utils/get-provider-name';
import { AvatarSizes, renderAvatar } from 'utils/render-avatar';
import { applyFilters as applyFiltersPipeline } from '../../filter-system';
import type { FilterState } from '../../filter-system';
import { getDataLogStatusLabel, getDateOptionLabel, getViesStatusLabel } from 'utils/display-enum-label';
import { getAccountTypeLabel } from 'i18n/accountTypeLabel';
import { formatBadgeCount, formatDisplayInteger } from 'i18n/arabicDigits';
import {
  formatDisplayDate,
  getLocalizedDatePickerFormat,
} from 'i18n/calendarLabels';
import {
  getCountryLabelFromCountries,
  localizeCountryInCompositeLabel,
} from 'i18n/countryLabel';
import { localizeReturnMappingLabel } from 'i18n/returnTypeLabel';

export type { FilterState };

const resolveStatusForLabel = (status: string): string => {
  if (!status || status === 'NOT_STARTED') {
    return DataLogStatus.NOT_STARTED;
  }
  const enumValue = DataLogStatus[status as keyof typeof DataLogStatus];
  return enumValue && enumValue !== status ? enumValue : status;
};

export const USER_NAME_FILTERS = [
  'accountCreatorName',
  'users',
  'user',
  'preparer',
  'reviewer',
  'client',
  'submittedBy',
];

export const TRANSACTIONS_NUMBER_FILTER_KEYS = ['net', 'tax', 'gross', 'taxRate'];

/** Free-text search filters for entity transactions (API: `{ description: ["tran"] }`). */
export const TRANSACTIONS_TEXT_FILTER_KEYS = ['description'];

export const Unassigned = 'unassigned';

const FilterDropdownStyle = createGlobalStyle`
  .scope-table-filter-dropdown.ant-select-dropdown {
    min-width: 275px;
    max-width: min(95vw, calc(100vw - 32px));
  }

  .app-root--pwa-mobile .scope-table-filter-dropdown.ant-select-dropdown {
    min-width: 0 !important;
    width: calc(
      100vw - 2 * var(--pwa-content-gutter, 16px) - var(--pwa-safe-left, 0px) -
        var(--pwa-safe-right, 0px)
    ) !important;
    max-width: calc(
      100vw - 2 * var(--pwa-content-gutter, 16px) - var(--pwa-safe-left, 0px) -
        var(--pwa-safe-right, 0px)
    ) !important;
    left: calc(var(--pwa-content-gutter, 16px) + var(--pwa-safe-left, 0px)) !important;
    right: calc(var(--pwa-content-gutter, 16px) + var(--pwa-safe-right, 0px)) !important;
    box-sizing: border-box !important;
  }
`;

const ClearFiltersButton = styled(LinkButton)`
  text-decoration: none !important;
  color: var(--color-text-base, #042a2a) !important;

  &:not(:disabled):not(.ant-btn-disabled):hover,
  &:not(:disabled):not(.ant-btn-disabled):focus {
    color: var(--color-primary, #41aaaf) !important;
    background: transparent !important;
    text-decoration: none !important;
  }
`;

const { RangePicker } = DatePicker;
const dateFormat = 'DD/MM/YYYY';
const CustomDateFormat = 'MM/DD/YYYY';
const ISO_DATE_FORMAT = 'YYYY-MM-DD';
dayjs.extend(require('dayjs/plugin/quarterOfYear'));

const getOperators = (t: (key: string) => string) => [
  { label: t('main.filter-operator-larger-than'), value: '>' },
  { label: t('main.filter-operator-smaller-than'), value: '<' },
  { label: t('main.filter-operator-larger-or-equal'), value: '>=' },
  { label: t('main.filter-operator-smaller-or-equal'), value: '<=' },
  { label: t('main.filter-operator-equal-to'), value: '=' },
  { label: t('main.filter-operator-not-equal-to'), value: '!=' },
];

export interface ScopeTableFiltersProps {
  filters: string[];
  visibleFilters?: string[];
  value: FilterState;
  onChange: (nextState: FilterState) => void;
  originalDataSource?: any[];
  tableDataSource?: any[];
  dataSource?: any[];
  mainLocaleKey?: string;
  customFilterOptions?: Record<
    string,
    { label: string; value: string | number }[]
  >;
  onOpenFilterSelection?: () => void;
  filtersCollapsedByDefault?: boolean;
  filterExtraContent?: React.ReactNode;
  appendedFilterComponent?: React.JSX.Element;
  clearAppendedFilterComponent?: () => void;
  onClearFilters?: () => void;
  preserveUserOnClear?: boolean;
  cardFilterConfig?: any;
  updateStatusFilter?: boolean;
  setUpdateStatusFilter?: Dispatch<SetStateAction<any>>;
  initialStatusFilter?: string[];
  updateDueDateFilter?: boolean;
  setUpdateDueDateFilter?: Dispatch<SetStateAction<any>>;
  setSelectedContanier?: Dispatch<SetStateAction<string>>;
  selectedContanier?: string;
  selectedPresetFilters?: Array<Record<string, string>>;
  statusFilterKey?: string;
  selectedFilter?: Record<string, string>;
  customPagination?: boolean;
  backendFilters?: Record<string, unknown>;
  onServerFilter?: (filters: FilterState, pageSize?: number) => void;
  embedded?: boolean;
  onApplyData?: (nextState: FilterState, filteredData?: any[]) => void;
  setHasFilters?: (val: boolean) => void;
  /** Bottom spacing below the filter row (default 16). Use 0 when filters sit directly above ScopeTable. */
  filterRowMarginBottom?: number;
  /** Renders on the same row as filters, aligned to the end (e.g. page totals). */
  filterTrailingContent?: React.ReactNode;
  /** Optional compact action rendered on the mobile footer row (right side). */
  footerRightContent?: React.ReactNode;
  /** Match dropdown width to the trigger (mobile/PWA full-width filters). */
  matchTriggerWidth?: boolean;
}

const ScopeTableFiltersInner: React.FC<ScopeTableFiltersProps> = ({
  filters,
  visibleFilters,
  value,
  onChange,
  originalDataSource,
  tableDataSource,
  dataSource,
  mainLocaleKey,
  customFilterOptions,
  onOpenFilterSelection,
  filtersCollapsedByDefault = false,
  filterExtraContent,
  filterRowMarginBottom = 16,
  filterTrailingContent,
  footerRightContent,
  matchTriggerWidth = false,
  appendedFilterComponent,
  clearAppendedFilterComponent,
  onClearFilters,
  preserveUserOnClear = true,
  cardFilterConfig,
  updateStatusFilter,
  setUpdateStatusFilter,
  initialStatusFilter,
  updateDueDateFilter,
  setUpdateDueDateFilter,
  setSelectedContanier,
  selectedContanier,
  selectedPresetFilters,
  statusFilterKey,
  selectedFilter,
  customPagination,
  backendFilters: backendFiltersProp,
  onServerFilter,
  embedded = true,
  onApplyData,
  setHasFilters,
}) => {
  const { t, i18n } = useTranslation();
  const operators = getOperators(t);
  const countries = useSelector(getCommonCountries);
  const backendFiltersFromStore = useSelector(getReportFilters);
  const backendFilters = backendFiltersProp ?? backendFiltersFromStore;

  const [filtersState, setFiltersState] = useState<FilterState>(value ?? {});
  const [openFilter, setOpenFilter] = useState<Record<string, { value: boolean }>>({});
  const [enableCustomRange, setEnableCustomRange] = useState<Record<string, boolean>>({});
  const [unassignedFlag, setUnassignedFlag] = useState<Record<string, boolean>>({});
  const [dateRange, setDateRange] = useState<Record<string, any>>({});
  const [dateRangeOption, setDateRangeOption] = useState<Record<string, any>>({});
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [hovered, setHovered] = useState(false);
  const [hoveredFilter, setHoveredFilter] = useState('');
  const [badgeCounts, setBadgeCounts] = useState<Record<string, number>>({});
  const [filtersRowVisible, setFiltersRowVisible] = useState(false);
  const [searchValue, setSearchValue] = useState<{ key?: string; value?: string }>({});
  const [numberFilterByKey, setNumberFilterByKey] = useState<
    Record<string, { value: number | null; operator?: string }>
  >({});
  const [textFilterByKey, setTextFilterByKey] = useState<
    Record<string, string>
  >({});

  const pendingFilterValuesRef = useRef<Record<string, any>>({});
  const selectValueRefs = useRef<Record<string, any>>({});
  const userFilterFromCardConfigRef = useRef<any>(null);
  const valueAppliedRef = useRef<string>('');
  const initialStatusFilterAppliedRef = useRef<string>('');
  const isManualStatusChangeRef = useRef(false);
  const isInternalCommitRef = useRef(false);
  const dataSourceChangedRef = useRef(false);

  const prevSelectedFilters = usePrevious(selectedFilters);

  const resolveDataSource = useCallback(
    (useOriginal = false) => {
      if (useOriginal) return originalDataSource;
      if (
        dataSource &&
        dataSource.length > 0 &&
        originalDataSource &&
        dataSource.length !== originalDataSource.length
      ) {
        return dataSource;
      }
      if (
        tableDataSource &&
        tableDataSource.length > 0 &&
        originalDataSource &&
        tableDataSource.length !== originalDataSource.length
      ) {
        return tableDataSource;
      }
      return originalDataSource ?? tableDataSource ?? dataSource;
    },
    [dataSource, originalDataSource, tableDataSource]
  );

  const optionsDataSource = useMemo(
    () => resolveDataSource(true),
    [resolveDataSource]
  );

  const memoizedPeriodOptions = useMemo(() => {
    const periodSet = new Map<string, { from: string; to: string }>();
    optionsDataSource?.forEach((item) => {
      const fromDate = item.dataLog?.fromReportingDate;
      const toDate = item.dataLog?.toReportingDate;
      if (fromDate && toDate) {
        const periodKey = `${fromDate}_${toDate}`;
        if (!periodSet.has(periodKey)) {
          periodSet.set(periodKey, { from: fromDate, to: toDate });
        }
      }
    });
    return Array.from(periodSet.values()).sort(
      (a, b) => new Date(b.from).getTime() - new Date(a.from).getTime()
    );
  }, [optionsDataSource]);

  const memoizedStatusData = useMemo(() => {
    const statusSet = new Set<string>();
    let notStartedCount = 0;
    const statusCounts = new Map<string, number>();

    optionsDataSource?.forEach((item) => {
      const status = item.status;
      if (!status || status.trim() === '') {
        notStartedCount += 1;
        return;
      }
      statusSet.add(status);
      statusCounts.set(status, (statusCounts.get(status) ?? 0) + 1);
    });

    return {
      statusValues: Array.from(statusSet).sort(),
      hasNotStarted: notStartedCount > 0,
      notStartedCount,
      statusCounts,
    };
  }, [optionsDataSource]);

  const getStatusOptionCount = useCallback(
    (status: string) => {
      if (status === 'NOT_STARTED') {
        return memoizedStatusData.notStartedCount;
      }
      return memoizedStatusData.statusCounts.get(status) ?? 0;
    },
    [memoizedStatusData]
  );

  const renderStatusOptionCount = (status: string) => (
    <span
      style={{
        background: '#41AAAF',
        color: '#fff',
        borderRadius: '50%',
        minWidth: 22,
        height: 22,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 12,
        fontWeight: 600,
        flexShrink: 0,
        padding: '0 4px',
      }}
    >
      {formatDisplayInteger(getStatusOptionCount(status), i18n.language)}
    </span>
  );

  const commitState = useCallback(
    (
      nextState: FilterState,
      options?: { filteredData?: any[]; serverOnly?: boolean }
    ) => {
      isInternalCommitRef.current = true;
      setFiltersState(nextState);
      onChange(nextState);

      if (setHasFilters) {
        setHasFilters(Object.keys(nextState).some((k) => k !== 'sort'));
      }

      if (!embedded) {
        if (customPagination || options?.serverOnly) {
          onServerFilter?.(nextState);
        }
        return;
      }

      if (customPagination || options?.serverOnly) {
        onServerFilter?.(nextState);
        return;
      }

      if (options?.filteredData !== undefined) {
        dataSourceChangedRef.current = true;
        onApplyData?.(nextState, options.filteredData);
        return;
      }

      const source = resolveDataSource();
      if (source) {
        const filtered = applyFiltersPipeline(source, nextState, {
          mainLocaleKey,
          excludeSubmittedCancelledForReturnType: true,
        });
        dataSourceChangedRef.current = true;
        onApplyData?.(nextState, filtered);
      } else {
        onApplyData?.(nextState);
      }
    },
    [
      customPagination,
      embedded,
      mainLocaleKey,
      onApplyData,
      onChange,
      onServerFilter,
      resolveDataSource,
      setHasFilters,
    ]
  );

  const translateFilter = useCallback(
    (filterValue: any, key: string, mode: 'key' | 'value' = 'key') => {
      if (customPagination && backendFilters && key && filterValue?.length > 0) {
        const searchIndex = mode === 'key' ? 0 : 1;
        const returnIndex = mode === 'key' ? 1 : 0;
        const options = backendFilters[key];
        if (
          Array.isArray(options) &&
          options.length > 0 &&
          Array.isArray(options[1])
        ) {
          const translated: string[] = [];
          for (const val of filterValue) {
            const index = options[searchIndex].indexOf(val);
            translated.push(index >= 0 ? options[returnIndex][index] : val);
          }
          return translated;
        }
      }
      return filterValue;
    },
    [backendFilters, customPagination]
  );

  const setFilterState = useCallback(
    (
      key: string,
      filterValue: any,
      allowManualClear = false,
      shouldCommit = false
    ) => {
      const isStatusFilter = key === 'status';
      let nextState: FilterState = { ...filtersState };

      if (typeof filterValue === 'object' && filterValue.length === 0) {
        if (isStatusFilter && !allowManualClear) return;
        delete nextState[key];
        setSelectedFilters((prev) => prev.filter((f) => f !== key));
      } else {
        let normalizedValue = filterValue;
        if (
          isStatusFilter &&
          filterValue &&
          Array.isArray(filterValue) &&
          filterValue.length > 0
        ) {
          normalizedValue = filterValue.map((status: string) => {
            const enumValue =
              DataLogStatus[status as keyof typeof DataLogStatus];
            return enumValue && enumValue !== status ? enumValue : status;
          });
          setSelectedFilters((prev) =>
            prev.find((f) => f.toLowerCase() === key.toLowerCase())
              ? prev
              : [...prev, key]
          );
        }
        nextState = { ...nextState, [key]: { value: normalizedValue } };
      }

      if (shouldCommit) {
        commitState(nextState);
      } else {
        setFiltersState(nextState);
      }
    },
    [commitState, filtersState]
  );

  const isNumberFilterKey = (key: string) =>
    key === 'netAmountConverted' ||
    (mainLocaleKey === 'transactions' &&
      TRANSACTIONS_NUMBER_FILTER_KEYS.includes(key));

  const isTextFilterKey = (key: string) =>
    mainLocaleKey === 'transactions' &&
    TRANSACTIONS_TEXT_FILTER_KEYS.includes(key);

  const getPlaceholder = (filterKey: string, fullFilterKey?: string) => {
    const labelKey = fullFilterKey ?? filterKey;
    const resolveNestedLabel = (
      labels: Record<string, any>,
      keyPath: string
    ): string | undefined => {
      return keyPath
        .split('.')
        .reduce<any>((acc, keyPart) => (acc ? acc[keyPart] : undefined), labels);
    };

    if (mainLocaleKey) {
      const labels = t(mainLocaleKey, { returnObjects: true }) as
        | Record<string, string>
        | string;
      if (labels && typeof labels === 'object') {
        const resolved = resolveNestedLabel(labels, labelKey);
        if (typeof resolved === 'string' && resolved.length > 0) {
          return resolved;
        }
      }
      const translated = t(`${mainLocaleKey}.${filterKey}`);
      if (translated !== `${mainLocaleKey}.${filterKey}`) return translated;
    }
    const mainRootKey = 'main';
    const mainRoot = t(mainRootKey, { returnObjects: true }) as
      | Record<string, any>
      | string;
    const mainLabels =
      mainRoot && typeof mainRoot === 'object'
        ? (mainRoot.filterLabels as Record<string, any> | undefined)
        : undefined;
    if (mainLabels && typeof mainLabels === 'object') {
      const resolved = resolveNestedLabel(mainLabels, labelKey);
      if (typeof resolved === 'string' && resolved.length > 0) {
        return resolved;
      }
    }
    const commonFallbackKey = getCommonLabelFallbackKey(filterKey);
    if (commonFallbackKey) {
      return t(commonFallbackKey);
    }
    return t(`main.${filterKey}`);
  };

  const getBadgeCount = (key: string) => {
    if (filtersState[key]?.value) {
      return typeof filtersState[key]?.value === 'object'
        ? (filtersState[key]?.value as any[])?.length
        : 1;
    }
    return false;
  };

  const renderFilterBadgeCount = (count: number) =>
    matchTriggerWidth ? 0 : formatBadgeCount(count, i18n.language);

  const getPopoverContent = (key: string) => {
    if (USER_NAME_FILTERS.includes(key)) {
      const userValue = filtersState[key]?.value;
      if (userValue && Array.isArray(userValue) && userValue.length > 0) {
        return userValue.map((userName: string, index: number) => (
          <React.Fragment key={index}>
            <span>{userName}</span>
            {index < userValue.length - 1 && <br />}
          </React.Fragment>
        ));
      }
      return userValue || '';
    }
    return typeof filtersState[key]?.value === 'object' &&
      (filtersState[key]?.value as any[])?.length > 0
      ? (filtersState[key]?.value as any[]).map((filter, idx) => (
          <React.Fragment key={idx}>
            <span>
              {filter === 'EMPTY'
                ? t('main.missing')
                : filter === 'NOT_STARTED'
                ? t('main.notStarted')
                : key === 'providerName'
                ? getProviderName(filter || '')
                : translateFilter([filter], key, 'value')?.[0] || filter}
            </span>
            <br />
          </React.Fragment>
        ))
      : filtersState[key]?.value === 'NOT_STARTED'
      ? t('main.notStarted')
      : key === 'providerName'
      ? getProviderName((filtersState[key]?.value as string) || '')
      : translateFilter(
          [filtersState[key]?.value as string],
          key,
          'value'
        )?.[0] || filtersState[key]?.value;
  };

  const hasCommittedFilterValue = (key: string) => {
    const filterValue = filtersState[key]?.value;
    if (!filterValue) return false;
    return Array.isArray(filterValue) ? filterValue.length > 0 : true;
  };

  const clearFilterDraftRefs = (key: string) => {
    delete pendingFilterValuesRef.current[key];
    delete selectValueRefs.current[key];
  };

  const getSelectedFilterClass = (key: string) =>
    !hasCommittedFilterValue(key) ? '' : 'selected-filter';

  const getPopupStyle = (key: string): React.CSSProperties => {
    if (matchTriggerWidth) {
      return {
        padding: '16px',
        paddingTop: '4px',
        fontSize: 13,
        borderRadius: 12,
        border: '1px solid var(--color-primary, #41aaaf)',
        background: 'var(--color-surface-raised, #ffffff)',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
        boxSizing: 'border-box',
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
      };
    }

    const baseStyle: React.CSSProperties = {
      minWidth: '280px',
      width: 'max-content',
      maxWidth: 'min(95vw, calc(100vw - 32px))',
      padding: '16px',
      paddingTop: '4px',
      fontSize: 13,
      borderRadius: 12,
      border: '1px solid var(--color-primary, #41aaaf)',
      background: 'var(--color-surface-raised, #ffffff)',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
      boxSizing: 'border-box',
    };
    if (key.toLocaleLowerCase().includes('mapping')) {
      return { ...baseStyle, minWidth: '280px' };
    }
    return baseStyle;
  };

  // On PWA mobile we mount all filter dropdowns into a fixed-position sentinel
  // div that spans the safe content area. Ant measures the trigger's offsetLeft
  // *within* that sentinel (always 0) so the popup can never be positioned
  // off-screen, and max-width: 100% fills the safe width automatically.
  const pwaDropdownSentinelRef = React.useRef<HTMLDivElement | null>(null);
  const getPwaPopupContainer = React.useCallback(
    (_trigger: HTMLElement) => {
      if (!pwaDropdownSentinelRef.current) {
        const el = document.createElement('div');
        el.className = 'scope-pwa-dropdown-sentinel';
        document.body.appendChild(el);
        pwaDropdownSentinelRef.current = el;
      }
      return pwaDropdownSentinelRef.current;
    },
    []
  );
  React.useEffect(() => {
    return () => {
      pwaDropdownSentinelRef.current?.remove();
      pwaDropdownSentinelRef.current = null;
    };
  }, []);

  const filterPopupLayoutProps = matchTriggerWidth
    ? ({ popupMatchSelectWidth: true, placement: 'bottomLeft' as const } as const)
    : {};

  const onSearch = (search: string, key: string) => {
    setSearchValue({ key, value: search });
  };

  const onUnassignedCheckboxChange = (e: any, originalFilterKey: string) => {
    setUnassignedFlag({ ...unassignedFlag, [originalFilterKey]: e.target.checked });
  };

  const onCustomRangeCheckboxChange = (e: any, filterKey: string) => {
    if (e.target.checked) {
      setEnableCustomRange({ ...enableCustomRange, [filterKey]: true });
      setDateRangeOption({
        ...dateRangeOption,
        [filterKey]: t('main.custom-range'),
      });
    } else {
      setEnableCustomRange({ ...enableCustomRange, [filterKey]: false });
      setDateRangeOption({ ...dateRangeOption, [filterKey]: [] });
    }
  };

  const removeDuplicate = (key: string) => {
    const keys = key.split('.');
    const dataToUse =
      originalDataSource && originalDataSource.length > 0
        ? originalDataSource
        : dataSource;

    if (keys[0] === 'user') {
      const allUsers: any[] = [];
      dataToUse?.forEach((item) => {
        if (item.dataLog?.preparerUser) allUsers.push(item.dataLog.preparerUser);
        if (item.dataLog?.reviewerUser) allUsers.push(item.dataLog.reviewerUser);
        if (item.dataLog?.clientUser) allUsers.push(item.dataLog.clientUser);
      });
      const uniqueUsers = _.uniqBy(allUsers, 'userUuid');
      if (searchValue?.key === 'user' && searchValue?.value) {
        return uniqueUsers.filter(
          (item: any) =>
            item &&
            (item.firstName + ' ' + item.lastName)
              .toLowerCase()
              .includes((searchValue.value || '').toLowerCase())
        );
      }
      return uniqueUsers.filter((item) => item);
    }

    if (keys[0] === 'country' && mainLocaleKey !== 'vat-checker') {
      const countryList = Array.from(
        new Set(dataToUse?.map((row) => row[keys[0]]))
      );
      if (searchValue?.key === keys[0] && searchValue?.value) {
        const val2Srch = searchValue.value || '';
        return countryList.map((countryISO) =>
          countries.find((c) => {
            if (c.iso !== countryISO) return false;
            const localizedName = getCountryLabelFromCountries(
              c.iso,
              countries,
              i18n.language,
              c.name
            );
            return (
              c.name.toLowerCase().includes(val2Srch.toLowerCase()) ||
              localizedName.toLowerCase().includes(val2Srch.toLowerCase())
            );
          })
        );
      }
      return countryList.map((countryISO) =>
        countries.find((c) => c.iso === countryISO)
      );
    }

    if (keys[0] === 'accountType') {
      const enumList = Array.from(new Set(dataToUse?.map((row) => row[keys[0]])));
      return enumList.map((item: AccountType) => {
        const label = item ? getAccountTypeLabel(String(item), t) : item;
        if (searchValue?.key === keys[0] && searchValue?.value) {
          if (
            (mainLocaleKey === 'transactions' &&
              item?.toString().toLowerCase().includes(searchValue.value.toLowerCase())) ||
            String(label)?.toLowerCase()?.includes(searchValue.value.toLowerCase())
          ) {
            return { name: label, value: item };
          }
          return undefined;
        }
        return { name: label, value: item };
      });
    }

    if (keys[0] === 'viesStatus') {
      const enumList = Array.from(new Set(dataToUse?.map((row) => row[keys[0]])));
      return enumList.map((item: ViesStatus) => {
        const enumValue =
          ViesStatus[item as keyof typeof ViesStatus] ?? item;
        const label = getViesStatusLabel(String(enumValue), t);
        if (searchValue?.key === keys[0] && searchValue?.value) {
          if (label.toLowerCase().includes(searchValue.value.toLowerCase())) {
            return { name: label, value: item };
          }
          return undefined;
        }
        return { name: label, value: item };
      });
    }

    if (keys[0] === 'submittedBy') {
      const allOptions: any[] = [];
      dataToUse?.forEach((item) => {
        const user = item.dataLog?.submittedByUser ?? item.submittedByUser;
        if (user && (user.firstName || user.email)) {
          allOptions.push(user);
          return;
        }

        if (Array.isArray(item.submittedBy)) {
          item.submittedBy.forEach((entry: any) => {
            if (
              entry &&
              typeof entry === 'object' &&
              (entry.firstName || entry.email)
            ) {
              allOptions.push(entry);
            } else if (typeof entry === 'string' && entry.trim()) {
              allOptions.push(entry.trim());
            }
          });
          return;
        }

        const name = item.submittedByName ?? item.dataLog?.submittedBy ?? null;
        if (name && typeof name === 'string' && name.trim()) {
          allOptions.push(name.trim());
        }
      });

      const uniqueUsers = _.uniqBy(
        allOptions.filter((option) => typeof option === 'object'),
        (user) => user.userUuid || user.email
      );
      const uniqueNames = _.uniq(
        allOptions.filter((option) => typeof option === 'string') as string[]
      );
      const combined = [...uniqueUsers, ...uniqueNames];

      if (searchValue?.key === 'submittedBy' && searchValue?.value) {
        const searchVal = searchValue.value || '';
        return combined.filter((item: any) => {
          if (!item) return false;
          if (typeof item === 'string') {
            return item.toLowerCase().includes(searchVal.toLowerCase());
          }
          return (
            (item.firstName &&
              item.lastName &&
              (item.firstName + ' ' + item.lastName)
                .toLowerCase()
                .includes(searchVal.toLowerCase())) ||
            (item.email &&
              item.email.toLowerCase().includes(searchVal.toLowerCase()))
          );
        });
      }
      return combined.filter(
        (item) =>
          item &&
          (typeof item === 'string' || item.firstName || item.email)
      );
    }

    if (keys[0] === 'mappingName') {
      const names: string[] = [];
      dataToUse?.forEach((row) => {
        if (Array.isArray(row?.returns)) {
          row.returns.forEach((ret: { mappingName?: string }) => {
            if (ret?.mappingName) names.push(ret.mappingName);
          });
        } else if (row?.mappingName) {
          names.push(row.mappingName);
        }
      });
      const unique = _.uniq(names.filter(Boolean));
      if (searchValue?.key === keys[0] && searchValue?.value) {
        return unique.filter((name) =>
          name.toLowerCase().includes((searchValue.value || '').toLowerCase())
        );
      }
      return unique;
    }

    if (
      ((originalDataSource?.length ?? 0) > 0 &&
        Array.isArray(originalDataSource?.[0]?.[keys[0]])) ||
      ((dataToUse?.length ?? 0) > 0 && Array.isArray(dataToUse?.[0]?.[keys[0]]))
    ) {
      const array: any[] = [];
      dataToUse?.forEach((row) => {
        const nested = row[keys[0]];
        if (!Array.isArray(nested)) return;
        nested.forEach((keyItem) => {
          array.push(keys[1] ? keyItem[keys[1]] : keyItem);
        });
      });

      if (USER_NAME_FILTERS.includes(key)) {
        const filtered = array.filter((item) => item != null);
        const searched =
          searchValue?.key === keys[0] && searchValue?.value
            ? filtered.filter((item) =>
                `${item.firstName || ''} ${item.lastName || ''}`
                  .trim()
                  .toLowerCase()
                  .includes((searchValue.value || '').toLowerCase())
              )
            : filtered;
        return _.uniqBy(searched, 'userUuid');
      }

      const filtered = array.filter((item) => item != null && item !== '');
      const searched =
        searchValue?.key === keys[0] && searchValue?.value
          ? filtered.filter((item) =>
              String(item)
                .toLowerCase()
                .includes((searchValue.value || '').toLowerCase())
            )
          : filtered;
      return _.uniq(searched);
    }

    if (
      ((originalDataSource?.length ?? 0) > 0 &&
        USER_NAME_FILTERS.includes(keys[0]) &&
        _.some(
          originalDataSource ?? [],
          (item) =>
            item[keys[0]] != null &&
            typeof item[keys[0]] === 'object' &&
            !Array.isArray(item[keys[0]])
        )) ||
      ((dataToUse?.length ?? 0) > 0 &&
        USER_NAME_FILTERS.includes(keys[0]) &&
        _.some(
          dataToUse ?? [],
          (item) =>
            item[keys[0]] != null &&
            typeof item[keys[0]] === 'object' &&
            !Array.isArray(item[keys[0]])
        ))
    ) {
      const distinctArray = Array.from(
        new Set(dataToUse?.map((row) => row[keys[0]]))
      );
      const filtered = distinctArray.filter((item) => item != null);
      const searched =
        searchValue?.key === keys[0] && searchValue?.value
          ? filtered.filter((item: any) =>
              `${item.firstName || ''} ${item.lastName || ''}`
                .trim()
                .toLowerCase()
                .includes((searchValue.value || '').toLowerCase())
            )
          : filtered;
      return _.uniqBy(searched, 'userUuid');
    }

    if (keys[0] === 'providerName' || keys[0] === 'provider') {
      const distinctArray = Array.from(
        new Set(dataToUse?.map((row) => row.providerName))
      );
      if (searchValue?.key === keys[0] && searchValue?.value) {
        return distinctArray.filter(
          (item: any) =>
            item &&
            getProviderName(item)
              ?.toLowerCase()
              ?.includes((searchValue.value || '').toLowerCase())
        );
      }
      return distinctArray.filter((item) => item);
    }

    const distinctArray = Array.from(
      new Set(dataToUse?.map((row) => row[keys[0]]))
    );
    if (searchValue?.key === keys[0] && searchValue?.value) {
      return distinctArray.filter(
        (item: any) =>
          item &&
          item?.toLowerCase?.().includes((searchValue.value || '').toLowerCase())
      );
    }
    return distinctArray.filter((item) => item);
  };

  const renderAvatarOption = (names: string[], key: string, optionValue: any) => {
    if (typeof optionValue === 'object') {
      const userId = optionValue?.userUuid;
      const firstName = optionValue?.firstName || '';
      const lastName = optionValue?.lastName || '';
      const userPic = optionValue?.profilePicture;
      const fullName = `${firstName} ${lastName}`.trim();
      return (
        <Select.Option
          key={userId}
          value={fullName}
          disabled={unassignedFlag[key] && USER_NAME_FILTERS.includes(key)}
        >
          <div style={{ display: 'flex' }}>
            {renderAvatar(firstName, lastName, AvatarSizes.DEFAULT, userId, userPic)}
            <div style={{ margin: '5px 12px' }}>{firstName + ' ' + lastName}</div>
          </div>
        </Select.Option>
      );
    }
    return (
      <Select.Option
        key={optionValue}
        value={optionValue}
        disabled={unassignedFlag[key] && USER_NAME_FILTERS.includes(key)}
      >
        <div style={{ display: 'flex' }}>
          {renderAvatar(names[0], names[1], AvatarSizes.DEFAULT, optionValue)}
          <div style={{ margin: '5px 12px' }}>{optionValue}</div>
        </div>
      </Select.Option>
    );
  };

  const renderFilterOptions = (key: string) => {
    const keys = key.split('.');
    const customOpts = customFilterOptions?.[keys[0]];
    if (customOpts?.length) {
      return customOpts.map((opt) => (
        <Select.Option
          key={String(opt.value)}
          value={opt.value}
          label={opt.label}
          title={String(opt.label)}
        >
          {opt.label}
        </Select.Option>
      ));
    }

    if (key === 'period' || keys[0] === 'period') {
      return memoizedPeriodOptions.map((period) => {
        const fromDate = formatDisplayDate(
          period.from,
          'DD/MM/YYYY',
          i18n.language
        );
        const toDate = formatDisplayDate(period.to, 'DD/MM/YYYY', i18n.language);
        const periodValue = `${period.from}_${period.to}`;
        return (
          <Select.Option key={periodValue} value={periodValue}>
            {`${fromDate} - ${toDate}`}
          </Select.Option>
        );
      });
    }

    if (key === 'status') {
      const options = memoizedStatusData.statusValues.map((status) => (
        <Select.Option key={status} value={status}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
              minWidth: 280,
            }}
          >
            <span>{getDataLogStatusLabel(resolveStatusForLabel(status), t)}</span>
            {renderStatusOptionCount(status)}
          </div>
        </Select.Option>
      ));
      if (memoizedStatusData.hasNotStarted) {
        options.push(
          <Select.Option key="NOT_STARTED" value="NOT_STARTED">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 16,
                minWidth: 280,
              }}
            >
              <span>{getDataLogStatusLabel(DataLogStatus.NOT_STARTED, t)}</span>
              {renderStatusOptionCount('NOT_STARTED')}
            </div>
          </Select.Option>
        );
      }
      return options;
    }

    const selectOptions = removeDuplicate(key);
    let selectedOptions: any[] | undefined;
    if (!customPagination) {
      selectedOptions = selectOptions?.filter((item) => item !== undefined);
    } else if (backendFilters && backendFilters[key] != null) {
      selectedOptions = backendFilters[key] as any[];
      if (
        Array.isArray(selectedOptions) &&
        selectedOptions.length > 0 &&
        Array.isArray(selectedOptions[1])
      ) {
        selectedOptions = selectedOptions[0];
      }
    }
    if (!selectedOptions || selectedOptions.length === 0) {
      selectedOptions = selectOptions?.filter((item) => item !== undefined);
    }

    return Array.isArray(selectedOptions) && selectedOptions.length > 0
      ? selectedOptions.map((option) => {
          let names = keys[1] ? option[keys[1]]?.split(' ') : '';
          if (
            key === 'country' &&
            mainLocaleKey !== 'vat-checker' &&
            option &&
            option !== ''
          ) {
            const localizedCountryName = getCountryLabelFromCountries(
              option.iso,
              countries,
              i18n.language,
              option.name
            );
            return (
              <Select.Option
                key={option.iso}
                value={option.iso}
                title={localizedCountryName}
                label={getPlaceholder(key)}
              >
                {localizedCountryName}
              </Select.Option>
            );
          }
          if (typeof option === 'boolean') {
            return (
              <Select.Option key={option.toString()}>
                {option ? t('common.active') : t('common.not-active')}
              </Select.Option>
            );
          }
          if (key === 'sourceType') {
            return (
              <Select.Option key={option} value={option}>
                {SourceType[option] ?? option}
              </Select.Option>
            );
          }
          if (key === 'providerName' || key === 'provider') {
            const formattedProviderName = getProviderName(option);
            const optionLower = String(option).toLowerCase();
            const isGroup = optionLower === 'group';
            return (
              <Select.Option key={option} value={option}>
                <Space size={8} align="center">
                  {isGroup ? (
                    <UsergroupAddOutlined
                      style={{
                        fontSize: 20,
                        color: 'var(--color-text-base, #2e2e2e)',
                        verticalAlign: 'middle',
                        display: 'block',
                        width: 20,
                        height: 20,
                      }}
                      aria-hidden
                    />
                  ) : (
                    <ProviderIcon
                      providerName={String(option)}
                      width={20}
                      height={20}
                      alt={formattedProviderName}
                      style={{ verticalAlign: 'middle', display: 'block' }}
                    />
                  )}
                  <span>{formattedProviderName}</span>
                </Space>
              </Select.Option>
            );
          }
          if (typeof option === 'object' && Object.keys(option).includes(keys[1])) {
            return !USER_NAME_FILTERS.includes(key) ? (
              <Select.Option
                key={option[keys[1]]}
                value={option[keys[1]]}
                disabled={unassignedFlag[key] && USER_NAME_FILTERS.includes(key)}
              >
                {option[keys[1]]}
              </Select.Option>
            ) : (
              renderAvatarOption(names, key, option[keys[1]])
            );
          }
          if (typeof option === 'object' && !Object.keys(option).includes(keys[1])) {
            if (USER_NAME_FILTERS.includes(key) && option.firstName) {
              return renderAvatarOption(
                [option.firstName, option.lastName || ''],
                key,
                option
              );
            }
            return !USER_NAME_FILTERS.includes(key) ? (
              <Select.Option
                key={option.name}
                value={option.value}
                disabled={unassignedFlag[key] && USER_NAME_FILTERS.includes(key)}
              >
                {option.name}
              </Select.Option>
            ) : (
              renderAvatarOption(names, key, option)
            );
          }
          if (option && option !== '') {
            names = option?.split(' ');
            return !USER_NAME_FILTERS.includes(key) ? (
              <Select.Option
                key={option}
                value={option}
                disabled={unassignedFlag[key] && USER_NAME_FILTERS.includes(key)}
              >
                {key === 'mappingName' || key === 'returnName'
                  ? localizeReturnMappingLabel(
                      option,
                      countries,
                      i18n.language,
                      t
                    )
                  : option}
              </Select.Option>
            ) : (
              renderAvatarOption(names, key, option)
            );
          }
          return null;
        })
      : null;
  };

  const onFilterSelect = (
    key: string,
    filterValue: any,
    useOriginalDataSource = false,
    applied = false
  ) => {
    const keys = key?.split('.');
    const filterKey = keys[0];
    let nextState: FilterState = { ...filtersState };

    if (!filterValue || (Array.isArray(filterValue) && filterValue.length === 0)) {
      delete nextState[filterKey];
    } else {
      let normalizedValue = filterValue;
      if (
        filterKey === 'status' &&
        Array.isArray(filterValue) &&
        filterValue.length > 0
      ) {
        normalizedValue = filterValue.map((status: string) => {
          const enumValue = DataLogStatus[status as keyof typeof DataLogStatus];
          return enumValue && enumValue !== status ? enumValue : status;
        });
      }
      nextState = { ...nextState, [filterKey]: { value: normalizedValue } };
    }

    if (filterValue && (Array.isArray(filterValue) ? filterValue.length > 0 : true)) {
      setSelectedFilters((prev) =>
        prev.includes(filterKey) ? prev : [...prev, filterKey]
      );
    }

    if (!embedded) {
      commitState(nextState, { serverOnly: customPagination && applied });
      return;
    }

    if (customPagination && applied) {
      commitState(nextState, { serverOnly: true });
      return;
    }

    if (!filterValue || (Array.isArray(filterValue) && filterValue.length === 0)) {
      const currentDataSource = resolveDataSource(useOriginalDataSource);
      commitState(nextState, { filteredData: currentDataSource || [] });
      return;
    }

    const currentDataSource = resolveDataSource(
      useOriginalDataSource && filterKey !== 'status'
    );
    const pipelineState = { ...nextState };
    delete pipelineState.sort;
    const filteredData = applyFiltersPipeline(currentDataSource || [], pipelineState, {
      mainLocaleKey,
      excludeSubmittedCancelledForReturnType: true,
    });
    commitState(nextState, { filteredData });

    if (mainLocaleKey !== 'vat-checker' && setSelectedContanier && !updateStatusFilter) {
      setSelectedContanier('');
    }
    if (
      mainLocaleKey !== 'vat-checker' &&
      setSelectedContanier &&
      updateStatusFilter &&
      updateDueDateFilter
    ) {
      setSelectedContanier('');
    }
    if (
      setSelectedContanier &&
      filterKey === 'status' &&
      filterValue === DataLogStatus.AWAITING_CLIENT_APPROVAL
    ) {
      setSelectedContanier(dashboardStatistic.awaitingClient);
    }
  };

  const handleFilterChange = (key: string, filterValue: string[]) => {
    pendingFilterValuesRef.current[key] = filterValue;
    if (key === 'status') {
      isManualStatusChangeRef.current = true;
      setUpdateStatusFilter?.(false);
      dataSourceChangedRef.current = false;
      const nextState: FilterState = {
        ...filtersState,
        [key]: { value: translateFilter(filterValue, key) },
      };
      commitState(nextState);
      return;
    }
    setFilterState(key, translateFilter(filterValue, key), true);
  };

  const handleOnClickApply = (filterKey: string, originalFilterKey: string) => {
    if (mainLocaleKey !== 'vat-checker') setSelectedContanier?.('');
    const filterKeyBase = originalFilterKey.split('.')[0];
    const filterValue = unassignedFlag[originalFilterKey]
      ? Unassigned
      : selectValueRefs.current[filterKeyBase] ?? filtersState[filterKeyBase]?.value;
    onFilterSelect(originalFilterKey, filterValue, true, true);
    setSearchValue({});
    clearFilterDraftRefs(filterKeyBase);
    setOpenFilter({ ...openFilter, [filterKey]: { value: false } });
  };

  const handleOnClickFilterCancel = (filterKey: string, originalFilterKey: string) => {
    const baseKey = originalFilterKey.split('.')[0];
    let nextState: FilterState = { ...filtersState };
    delete nextState[baseKey];
    setSelectedFilters((prev) => prev.filter((f) => f !== baseKey));
    setUnassignedFlag({ ...unassignedFlag, [originalFilterKey]: false });
    setOpenFilter({ ...openFilter, [filterKey]: { value: false } });
    setSearchValue({});
    clearFilterDraftRefs(baseKey);
    setSelectedContanier?.('');
    commitState(nextState);
  };

  const clearFilters = () => {
    if (!preserveUserOnClear) userFilterFromCardConfigRef.current = null;
    const userFilterToPreserve = preserveUserOnClear
      ? userFilterFromCardConfigRef.current
      : null;
    const nextState: FilterState = {};
    if (filtersState.sort) nextState.sort = filtersState.sort;
    if (userFilterToPreserve) nextState.user = userFilterToPreserve;
    setSelectedFilters(userFilterToPreserve ? ['user'] : []);
    setBadgeCounts({});
    setDateRangeOption({});
    setDateRange({});
    setUnassignedFlag({});
    setNumberFilterByKey({});
    setTextFilterByKey({});
    pendingFilterValuesRef.current = {};
    selectValueRefs.current = {};
    setSelectedContanier?.('');
    clearAppendedFilterComponent?.();
    onClearFilters?.();
    dataSourceChangedRef.current = false;
    commitState(
      nextState,
      embedded && !customPagination
        ? { filteredData: originalDataSource || [] }
        : { serverOnly: customPagination }
    );
  };

  const onDateFilterChange = (filterKey: string, dateOverride?: any[]) => {
    const currentDateRange = dateOverride || dateRange[filterKey] || [];
    const fullKey =
      filters.find((k) => {
        const parts = k.split('.');
        return (parts.length > 1 ? parts[1] : parts[0]) === filterKey;
      }) || filterKey;

    const getNestedValue = (item: any, nestedKey: string) => {
      if (!nestedKey.includes('.')) return item[nestedKey];
      return nestedKey.split('.').reduce((val, part) => val?.[part], item);
    };

    let fromDate = '';
    let toDate = '';
    if (currentDateRange?.length > 0 && currentDateRange[0] && currentDateRange[1]) {
      fromDate = currentDateRange[0].format(
        customPagination ? ISO_DATE_FORMAT : CustomDateFormat
      );
      toDate = currentDateRange[1].format(
        customPagination ? ISO_DATE_FORMAT : CustomDateFormat
      );
    }

    let nextState: FilterState = { ...filtersState };
    if (fromDate && toDate) {
      nextState = {
        ...nextState,
        [filterKey]: { value: [fromDate, toDate] },
      };
    } else {
      delete nextState[filterKey];
    }

    if (!embedded) {
      commitState(nextState);
      setOpenFilter({ ...openFilter, [filterKey]: { value: false } });
      return;
    }

    if (customPagination) {
      commitState(nextState, { serverOnly: true });
    } else {
      let sourceToUse = resolveDataSource();
      const isDueDateFilter =
        filterKey.toLowerCase().includes('duedate') ||
        fullKey.toLowerCase().includes('duedate');
      if (isDueDateFilter) {
        sourceToUse = sourceToUse?.filter(
          (item: any) =>
            item.status !== DataLogStatus.SUBMITTED &&
            item.status !== DataLogStatus.CANCELLED
        );
      }
      let filteredData = sourceToUse || [];
      if (fromDate && toDate) {
        const fromDateDayjs = dayjs(fromDate, CustomDateFormat).startOf('day');
        const toDateDayjs = dayjs(toDate, CustomDateFormat).startOf('day');
        filteredData =
          sourceToUse?.filter((item) => {
            const itemDate = getNestedValue(item, fullKey);
            if (!itemDate) return false;
            const itemDateDayjs = dayjs(itemDate);
            if (!itemDateDayjs.isValid()) return false;
            const itemDateStart = itemDateDayjs.startOf('day');
            return (
              itemDateStart.valueOf() >= fromDateDayjs.valueOf() &&
              itemDateStart.valueOf() <= toDateDayjs.valueOf()
            );
          }) || [];
        filteredData = filteredData.filter(
          (item: any) =>
            item.status !== DataLogStatus.SUBMITTED &&
            item.status !== DataLogStatus.CANCELLED
        );
      }
      commitState(nextState, { filteredData });
    }

    if (currentDateRange?.length > 0) {
      setSelectedFilters((prev) =>
        prev.includes(filterKey) ? prev : [...prev, filterKey]
      );
    }
    setOpenFilter({ ...openFilter, [filterKey]: { value: false } });
    if (setSelectedContanier && updateStatusFilter && updateDueDateFilter) {
      setSelectedContanier('');
    }
  };

  const onDateOptionSelect = (optionValue: DateOptions, filterKey: string) => {
    let date: any[] = ['', ''];
    const today = dayjs().format(dateFormat);
    let start = '';
    let end = '';
    switch (optionValue) {
      case DateOptions.TODAY:
        date = [dayjs(today, dateFormat), dayjs(today, dateFormat).add(1, 'day')];
        break;
      case DateOptions.THIS_WEEK:
        start = dayjs().startOf('week').format(dateFormat);
        end = dayjs().endOf('week').add(1, 'day').format(dateFormat);
        date = [dayjs(start, dateFormat), dayjs(end, dateFormat)];
        break;
      case DateOptions.THIS_MONTH:
        start = dayjs().startOf('month').format(dateFormat);
        end = dayjs().endOf('month').add(1, 'day').format(dateFormat);
        date = [dayjs(start, dateFormat), dayjs(end, dateFormat)];
        break;
      case DateOptions.THIS_QUARTER:
        start = dayjs().startOf('quarter').format(dateFormat);
        end = dayjs().endOf('quarter').add(1, 'day').format(dateFormat);
        date = [dayjs(start, dateFormat), dayjs(end, dateFormat)];
        break;
      case DateOptions.NEXT_WEEK:
        start = dayjs().add(1, 'week').startOf('week').format(dateFormat);
        end = dayjs().add(1, 'week').endOf('week').add(1, 'day').format(dateFormat);
        date = [dayjs(start, dateFormat), dayjs(end, dateFormat)];
        break;
      case DateOptions.NEXT_MONTH:
        start = dayjs().add(1, 'month').startOf('month').format(dateFormat);
        end = dayjs().add(1, 'month').endOf('month').add(1, 'day').format(dateFormat);
        date = [dayjs(start, dateFormat), dayjs(end, dateFormat)];
        break;
      case DateOptions.NEXT_QUARTER:
        start = dayjs().add(1, 'quarter').startOf('quarter').format(dateFormat);
        end = dayjs().add(1, 'quarter').endOf('quarter').add(1, 'day').format(dateFormat);
        date = [dayjs(start, dateFormat), dayjs(end, dateFormat)];
        break;
      case DateOptions.THIS_YEAR:
        start = dayjs().startOf('year').format(dateFormat);
        end = dayjs().endOf('year').add(1, 'day').format(dateFormat);
        date = [dayjs(start, dateFormat), dayjs(end, dateFormat)];
        break;
      case DateOptions.LAST_WEEK:
        start = dayjs().subtract(1, 'week').startOf('week').format(dateFormat);
        end = dayjs().subtract(1, 'week').endOf('week').add(1, 'day').format(dateFormat);
        date = [dayjs(start, dateFormat), dayjs(end, dateFormat)];
        break;
      case DateOptions.LAST_MONTH:
        start = dayjs().subtract(1, 'month').startOf('month').format(dateFormat);
        end = dayjs().subtract(1, 'month').endOf('month').add(1, 'day').format(dateFormat);
        date = [dayjs(start, dateFormat), dayjs(end, dateFormat)];
        break;
      case DateOptions.LAST_YEAR:
        start = dayjs().subtract(1, 'year').startOf('year').format(dateFormat);
        end = dayjs().subtract(1, 'year').endOf('year').add(1, 'day').format(dateFormat);
        date = [dayjs(start, dateFormat), dayjs(end, dateFormat)];
        break;
      default:
        date = ['', ''];
    }
    setDateRange({ ...dateRange, [filterKey]: date });
    setDateRangeOption({ ...dateRangeOption, [filterKey]: optionValue });
    if (date && date.length === 2 && date[0] && date[1]) {
      if (!embedded) {
        const dateFormatForCommit = customPagination
          ? ISO_DATE_FORMAT
          : CustomDateFormat;
        const from = date[0].format(dateFormatForCommit);
        const to = date[1].format(dateFormatForCommit);
        commitState({ ...filtersState, [filterKey]: { value: [from, to] } });
      } else if (!customPagination) {
        onDateFilterChange(filterKey, date);
      } else {
        onDateFilterChange(filterKey, date);
      }
      setSelectedFilters((prev) => {
        const fullKey =
          filters.find((k) => {
            const parts = k.split('.');
            return (parts.length > 1 ? parts[1] : parts[0]) === filterKey;
          }) || filterKey;
        return prev.includes(fullKey) || prev.includes(filterKey)
          ? prev
          : [...prev, fullKey];
      });
    }
  };

  const handleOnClickDateCancel = (filterKey: string) => {
    setOpenFilter({ ...openFilter, [filterKey]: { value: false } });
    const newDateRange = { ...dateRange };
    delete newDateRange[filterKey];
    setDateRange(newDateRange);
    const newDateRangeOption = { ...dateRangeOption };
    delete newDateRangeOption[filterKey];
    setDateRangeOption(newDateRangeOption);
    setSelectedFilters((prev) => prev.filter((f) => f !== filterKey));
    const nextState = { ...filtersState };
    delete nextState[filterKey];
    setUpdateDueDateFilter?.(false);
    commitState(
      nextState,
      embedded && !customPagination
        ? { filteredData: originalDataSource || [] }
        : { serverOnly: customPagination }
    );
  };

  const onNetAmountFilterChange = (filterKey: string) => {
    const entry = numberFilterByKey[filterKey];
    const nextState: FilterState = { ...filtersState };
    if (entry?.value !== undefined && entry?.value !== null) {
      const filterValue: (string | number)[] = [entry.value];
      if (entry.operator) filterValue.push(entry.operator);
      nextState[filterKey] = {
        value: filterValue as unknown as FilterState[string]['value'],
      };
      setSelectedFilters((prev) =>
        prev.includes(filterKey) ? prev : [...prev, filterKey]
      );
    } else {
      delete nextState[filterKey];
    }
    if (!embedded) {
      commitState(nextState);
    } else if (customPagination) {
      commitState(nextState, { serverOnly: true });
    } else {
      const filtered = applyFiltersPipeline(resolveDataSource() || [], nextState, {
        mainLocaleKey,
        excludeSubmittedCancelledForReturnType: true,
      });
      commitState(nextState, { filteredData: filtered });
    }
    setOpenFilter({ ...openFilter, [filterKey]: { value: false } });
  };

  const handleOnClickNetAmountCancel = (filterKey: string) => {
    setOpenFilter({ ...openFilter, [filterKey]: { value: false } });
    setNumberFilterByKey((prev) => {
      const next = { ...prev };
      delete next[filterKey];
      return next;
    });
    setSelectedFilters((prev) => prev.filter((f) => f !== filterKey));
    const nextState = { ...filtersState };
    delete nextState[filterKey];
    commitState(nextState, { serverOnly: customPagination });
  };

  const onTextFilterChange = (filterKey: string) => {
    const raw = (textFilterByKey[filterKey] ?? '').trim();
    const nextState: FilterState = { ...filtersState };
    if (raw) {
      nextState[filterKey] = {
        value: [raw] as unknown as FilterState[string]['value'],
      };
      setSelectedFilters((prev) =>
        prev.includes(filterKey) ? prev : [...prev, filterKey]
      );
    } else {
      delete nextState[filterKey];
      setSelectedFilters((prev) => prev.filter((f) => f !== filterKey));
    }
    if (!embedded) {
      commitState(nextState);
    } else if (customPagination) {
      commitState(nextState, { serverOnly: true });
    } else {
      const filtered = applyFiltersPipeline(resolveDataSource() || [], nextState, {
        mainLocaleKey,
        excludeSubmittedCancelledForReturnType: true,
      });
      commitState(nextState, { filteredData: filtered });
    }
    setOpenFilter({ ...openFilter, [filterKey]: { value: false } });
  };

  const handleOnClickTextFilterCancel = (filterKey: string) => {
    setOpenFilter({ ...openFilter, [filterKey]: { value: false } });
    setTextFilterByKey((prev) => {
      const next = { ...prev };
      delete next[filterKey];
      return next;
    });
    setSelectedFilters((prev) => prev.filter((f) => f !== filterKey));
    const nextState = { ...filtersState };
    delete nextState[filterKey];
    commitState(nextState, { serverOnly: customPagination });
  };

  const handleOpenNetAmountFilterDropdown = (open: boolean, filterKey: string) => {
    setOpenFilter({ ...openFilter, [filterKey]: { value: open } });
  };

  const handleOpenTextFilterDropdown = (open: boolean, filterKey: string) => {
    setOpenFilter({ ...openFilter, [filterKey]: { value: open } });
  };

  const handleOpenDateFilterDropdown = (open: boolean, filterKey: string) => {
    setOpenFilter({ ...openFilter, [filterKey]: { value: open } });
  };

  const handleOpenFilterDropdown = (open: boolean, filterKey: string) => {
    if (!open) {
      setSearchValue({});
      clearFilterDraftRefs(filterKey);

      if (hasCommittedFilterValue(filterKey)) {
        setSelectedFilters((prev) =>
          prev.find((f) => f.toLowerCase() === filterKey.toLowerCase())
            ? prev
            : [...prev, filterKey]
        );
      } else {
        setSelectedFilters((prev) =>
          prev.filter((f) => f.toLowerCase() !== filterKey.toLowerCase())
        );
        if (filtersState[filterKey]) {
          setFilterState(filterKey, [], true);
        }
      }
    }
    setOpenFilter({ ...openFilter, [filterKey]: { value: open } });
  };

  const renderNetAmountFilter = (filterKey: string) => {
    const entry = numberFilterByKey[filterKey];
    const setEntry = (val: number | null, operator?: string) =>
      setNumberFilterByKey((prev) => ({
        ...prev,
        [filterKey]: { value: val ?? null, operator },
      }));
    return (
      <Space
        onMouseDown={(e) => e.stopPropagation()}
        direction="vertical"
        style={{ padding: '12px' }}
      >
        <FormSelect
          width="100%"
          height="35px"
          radius="10px"
          isFilter
          classNames={{ popup: { root: 'scope-table-filter-dropdown' } }}
          placeholder={t('other.select-operator')}
          style={{
            fontSize: 13,
            width: '100%',
          }}
          onChange={(op) => setEntry(entry?.value ?? null, op as string)}
          value={entry?.operator}
          allowClear
        >
          {operators.map((op) => (
            <Select.Option key={op.value} value={op.value}>
              {op.label}
            </Select.Option>
          ))}
        </FormSelect>
        <InputNumber
          placeholder={t('transactions.net')}
          value={entry?.value ?? undefined}
          onChange={(val) => setEntry(val ?? null, entry?.operator)}
          style={{ width: '100%' }}
          className="scope-table-filter-number-input no-spinner"
          controls={false}
        />
        <Space style={{ float: 'inline-end', marginTop: '10px' }}>
          <ScopeButton
            width="75px"
            height="25px"
            fontSize="12px"
            lineheight="12px"
            onClick={() => handleOnClickNetAmountCancel(filterKey)}
            type="default"
          >
            {t('common.cancel')}
          </ScopeButton>
          <ScopeButton
            width="75px"
            height="25px"
            fontSize="12px"
            lineheight="12px"
            onClick={() => onNetAmountFilterChange(filterKey)}
            type="primary"
          >
            {t('common.apply')}
          </ScopeButton>
        </Space>
      </Space>
    );
  };

  const renderTextFilter = (filterKey: string) => {
    const draft = textFilterByKey[filterKey] ?? '';
    const searchPlaceholder =
      filterKey === 'description'
        ? t('transactions.search-description-placeholder')
        : `${t('common.search')} ${getPlaceholder(filterKey)}`;
    return (
      <Space
        onMouseDown={(e) => e.stopPropagation()}
        direction="vertical"
        style={{ padding: '12px', minWidth: 280 }}
      >
        <RoundInput
          prefix={
            <SearchOutlined
              style={{ color: 'var(--color-text-muted, #5a6a6a)' }}
            />
          }
          placeholder={searchPlaceholder}
          value={draft}
          onChange={(e) =>
            setTextFilterByKey((prev) => ({
              ...prev,
              [filterKey]: e.target.value,
            }))
          }
          onPressEnter={() => onTextFilterChange(filterKey)}
          allowClear
          autoFocus
          bg_color="#ffffff"
          width="100%"
          height="34px"
          style={{ fontSize: 11 }}
        />
        <Space style={{ float: 'inline-end', marginTop: '10px' }}>
          <ScopeButton
            width="75px"
            height="25px"
            fontSize="12px"
            lineheight="12px"
            onClick={() => handleOnClickTextFilterCancel(filterKey)}
            type="default"
          >
            {t('common.cancel')}
          </ScopeButton>
          <ScopeButton
            width="75px"
            height="25px"
            fontSize="12px"
            lineheight="12px"
            onClick={() => onTextFilterChange(filterKey)}
            type="primary"
          >
            {t('common.apply')}
          </ScopeButton>
        </Space>
      </Space>
    );
  };

  const renderDateFilter = (filterKey: string) => {
    const currentDateRange = dateRange[filterKey];
    const currentDateRangeOption = dateRangeOption[filterKey];
    const isCustomRangeEnabled = enableCustomRange[filterKey] || false;
    const rangePickerValue =
      currentDateRange && currentDateRange.length > 0
        ? currentDateRange
        : undefined;
    return (
      <Space
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        direction="vertical"
        style={{ padding: '20px' }}
      >
        <FormSelect
          width="100%"
          height="35px"
          radius="10px"
          isFilter
          classNames={{ popup: { root: 'scope-table-filter-dropdown' } }}
          placeholder={getPlaceholder(filterKey)}
          style={{ backgroundColor: 'var(--color-surface, #F3F7F8)', borderColor: '#cccccc', fontSize: 13 }}
          onChange={(val) => onDateOptionSelect(val as DateOptions, filterKey)}
          value={currentDateRangeOption || undefined}
        >
          {Object.values(DateOptions).map((opt) => (
            <Select.Option key={opt} value={opt}>
              {getDateOptionLabel(opt, t)}
            </Select.Option>
          ))}
        </FormSelect>
        <ScopeCheckbox onChange={(e) => onCustomRangeCheckboxChange(e, filterKey)}>
          {t('main.custom-range')}
        </ScopeCheckbox>
        <RangePicker
          style={{ borderRadius: '10px', height: '34px' }}
          value={rangePickerValue}
          disabled={!isCustomRangeEnabled}
          format={getLocalizedDatePickerFormat(dateFormat, i18n.language)}
          onChange={(date) => setDateRange({ ...dateRange, [filterKey]: date })}
        />
        <Space style={{ float: 'inline-end', marginTop: '10px' }}>
          <ScopeButton
            width="75px"
            height="25px"
            fontSize="12px"
            lineheight="12px"
            onClick={() => handleOnClickDateCancel(filterKey)}
            type="default"
          >
            {t('common.cancel')}
          </ScopeButton>
          <ScopeButton
            width="75px"
            height="25px"
            fontSize="12px"
            lineheight="12px"
            onClick={() => onDateFilterChange(filterKey)}
            type="primary"
          >
            {t('common.apply')}
          </ScopeButton>
        </Space>
      </Space>
    );
  };

  const renderMultiFilter = (
    items: React.ReactNode,
    filterKey: string,
    originalFilterKey: string
  ): React.ReactElement => {
    const hasData =
      customPagination ||
      (originalDataSource && originalDataSource.length > 0) ||
      (dataSource && dataSource.length > 0);
    if (!hasData && !customPagination) return <div />;
    return (
      <div
        style={
          matchTriggerWidth
            ? {
                minWidth: 0,
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
              }
            : {
                minWidth: 380,
                width: 'max-content',
                maxWidth: 'min(90vw, calc(100vw - 32px))',
              }
        }
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <Space direction="vertical" style={{ width: '100%', paddingBottom: '10px' }}>
          {items}
          <Divider style={{ margin: '8px 0' }} />
          {USER_NAME_FILTERS.includes(originalFilterKey) ? (
            <ScopeCheckbox
              style={{ marginBottom: '8px', marginLeft: '10px', fontSize: '13px' }}
              checked={unassignedFlag[originalFilterKey]}
              onChange={(e) => onUnassignedCheckboxChange(e, originalFilterKey)}
            >
              {t('main.unassigned')}
            </ScopeCheckbox>
          ) : null}
          <Space style={{ float: 'inline-end', margin: '0 10px' }}>
            <ScopeButton
              width="75px"
              height="25px"
              fontSize="12px"
              lineheight="12px"
              onClick={() => handleOnClickFilterCancel(filterKey, originalFilterKey)}
              type="default"
            >
              {t('common.clear')}
            </ScopeButton>
            <ScopeButton
              width="75px"
              height="25px"
              fontSize="12px"
              lineheight="12px"
              onClick={() => handleOnClickApply(filterKey, originalFilterKey)}
              type="primary"
            >
              {t('common.apply')}
            </ScopeButton>
          </Space>
        </Space>
      </div>
    );
  };

  useEffect(() => {
    if (isInternalCommitRef.current) {
      isInternalCommitRef.current = false;
      return;
    }
    setFiltersState(value ?? {});
    const stateKey = JSON.stringify(value ?? {});
    if (valueAppliedRef.current === stateKey) return;
    valueAppliedRef.current = stateKey;

    const selectedFilterKeys = Object.keys(value ?? {}).filter((key) => {
      if (key === 'sort') return false;
      const v = value[key]?.value;
      return v && (Array.isArray(v) ? v.length > 0 : true);
    });
    setSelectedFilters(selectedFilterKeys);

    if (value?.nextDueDate?.value) {
      const nextDueDateValue = value.nextDueDate.value;
      if (
        typeof nextDueDateValue === 'string' &&
        Object.values(DateOptions).includes(nextDueDateValue as DateOptions)
      ) {
        setDateRangeOption((prev) => ({ ...prev, nextDueDate: nextDueDateValue }));
      } else if (Array.isArray(nextDueDateValue) && nextDueDateValue.length === 2) {
        const startDate = dayjs(nextDueDateValue[0], CustomDateFormat);
        const endDate = dayjs(nextDueDateValue[1], CustomDateFormat);
        if (startDate.isValid() && endDate.isValid()) {
          setDateRange((prev) => ({ ...prev, nextDueDate: [startDate, endDate] }));
        }
      }
    }
  }, [value]);

  useEffect(() => {
    if (!preserveUserOnClear) return;
    if (value?.user?.value) {
      userFilterFromCardConfigRef.current = value.user;
    } else if (value === null || (value && !value.user)) {
      const isTypeOfReturnsFilter = !!(
        cardFilterConfig?.filters?.mappingName || cardFilterConfig?.filters?.returnName
      );
      if (!isTypeOfReturnsFilter) userFilterFromCardConfigRef.current = null;
    }
  }, [value, preserveUserOnClear, cardFilterConfig]);

  useEffect(() => {
    const numberKeys = [
      'netAmountConverted',
      ...(mainLocaleKey === 'transactions' ? TRANSACTIONS_NUMBER_FILTER_KEYS : []),
    ];
    setNumberFilterByKey((prev) => {
      const next = { ...prev };
      numberKeys.forEach((k) => {
        if (openFilter[k]?.value) return;
        const v = filtersState[k]?.value;
        if (Array.isArray(v) && v.length >= 1 && typeof v[0] === 'number') {
          next[k] = {
            value: v[0],
            operator: typeof v[1] === 'string' ? v[1] : undefined,
          };
        } else {
          delete next[k];
        }
      });
      return next;
    });
  }, [filtersState, mainLocaleKey, openFilter]);

  useEffect(() => {
    if (mainLocaleKey !== 'transactions') return;
    setTextFilterByKey((prev) => {
      const next = { ...prev };
      TRANSACTIONS_TEXT_FILTER_KEYS.forEach((k) => {
        if (openFilter[k]?.value) return;
        const v = filtersState[k]?.value;
        if (Array.isArray(v) && v.length >= 1 && typeof v[0] === 'string') {
          next[k] = v[0];
        } else {
          delete next[k];
        }
      });
      return next;
    });
  }, [filtersState, mainLocaleKey, openFilter]);

  useEffect(() => {
    if (selectedPresetFilters && selectedPresetFilters.length > 0) {
      selectedPresetFilters.forEach((preset) => {
        if (preset.value) {
          const nextState: FilterState = {
            ...filtersState,
            [preset.key]: { value: [preset.value] },
          };
          setSelectedFilters([preset.key]);
          setBadgeCounts({ [preset.key]: 1 });
          if (embedded) {
            const filterKey =
              filters.find((f) => f.includes(preset.key)) || preset.key;
            onFilterSelect(filterKey, preset.value, true, customPagination);
          } else {
            commitState(nextState);
          }
        }
      });
    } else if (selectedPresetFilters && selectedPresetFilters.length === 0) {
      setSelectedFilters([]);
      const userFilterToPreserve = userFilterFromCardConfigRef.current;
      setFiltersState(userFilterToPreserve ? { user: userFilterToPreserve } : {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selectedPresetFilters)]);

  useEffect(() => {
    if (
      selectedContanier === dashboardStatistic.taxReturns ||
      selectedContanier === dashboardStatistic.expiredReturns
    ) {
      setSelectedFilters([]);
      setDateRangeOption({});
    }
    if (
      selectedContanier &&
      selectedContanier !== '' &&
      selectedContanier !== dashboardStatistic.dueReturns &&
      selectedContanier !== dashboardStatistic.awaitingClient
    ) {
      const userFilterToPreserve = userFilterFromCardConfigRef.current;
      setFiltersState((prev) => {
        const base: FilterState = {};
        if (prev.sort) base.sort = prev.sort;
        if (userFilterToPreserve) base.user = userFilterToPreserve;
        return base;
      });
    }
  }, [selectedContanier]);

  useEffect(() => {
    if (statusFilterKey) {
      const nextState: FilterState = {
        ...filtersState,
        viesStatus: {
          value:
            statusFilterKey === VIESContactStatistic.StatusMissing
              ? ['EMPTY']
              : [
                  ViesStatus.ERROR,
                  ViesStatus.INVALID,
                  ViesStatus.SYSTEM_UNAVAILABLE,
                ],
        },
      };
      commitState(nextState);
    } else if (!selectedFilter?.value) {
      const nextState = { ...filtersState };
      delete nextState.viesStatus;
      setSelectedFilters((prev) => prev.filter((f) => f !== 'viesStatus'));
      commitState(nextState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilterKey]);

  useEffect(() => {
    if (updateDueDateFilter) {
      const userFilterToPreserve = userFilterFromCardConfigRef.current;
      const nextState: FilterState = userFilterToPreserve
        ? { user: userFilterToPreserve }
        : {};
      setDateRangeOption(DateOptions.ALL as unknown as Record<string, any>);
      const selectedKey = filters.find((f) =>
        f?.toLocaleLowerCase()?.includes('date')
      );
      if (selectedKey) {
        setSelectedFilters((prev) =>
          prev.includes(selectedKey) ? prev : [...prev, selectedKey]
        );
      }
      commitState(nextState);
    } else {
      setDateRangeOption({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateDueDateFilter]);

  useEffect(() => {
    if (updateStatusFilter && !isManualStatusChangeRef.current) {
      const currentKey = `${updateStatusFilter}-${JSON.stringify(
        initialStatusFilter
      )}-${tableDataSource?.length || 0}`;
      const statusInFiltersState =
        filtersState.status?.value &&
        Array.isArray(filtersState.status.value) &&
        filtersState.status.value.length > 0;
      if (
        statusInFiltersState &&
        initialStatusFilterAppliedRef.current?.startsWith('from-initialFiltersState')
      ) {
        setUpdateStatusFilter?.(false);
        return;
      }
      if (initialStatusFilterAppliedRef.current !== currentKey) {
        if (!statusInFiltersState) {
          const userFilterToPreserve = userFilterFromCardConfigRef.current;
          setFiltersState(userFilterToPreserve ? { user: userFilterToPreserve } : {});
        }
        if (initialStatusFilter && initialStatusFilter.length > 0) {
          if (!statusInFiltersState) onFilterSelect('status', initialStatusFilter, true);
        } else {
          const statusValues = Array.from(
            new Set(
              tableDataSource
                ?.map((item) => item.status)
                .filter((status) => status != null && status !== '')
            )
          );
          if (statusValues.length === 0) {
            const hasEmptyStatus = tableDataSource?.some(
              (item) => !item.status || item.status === ''
            );
            onFilterSelect(
              'status',
              hasEmptyStatus ? ['NOT_STARTED'] : [DataLogStatus.AWAITING_CLIENT_APPROVAL],
              true
            );
          } else {
            onFilterSelect('status', statusValues, true);
          }
        }
        initialStatusFilterAppliedRef.current = currentKey;
        setUpdateStatusFilter?.(false);
      }
    } else if (!updateStatusFilter) {
      initialStatusFilterAppliedRef.current = '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateStatusFilter, initialStatusFilter, tableDataSource]);

  useEffect(() => {
    if (
      selectedFilters.length > 0 &&
      prevSelectedFilters?.toString() !== selectedFilters.toString()
    ) {
      if (!updateStatusFilter && updateDueDateFilter) {
        const hasStatusFilter =
          filtersState.status?.value &&
          Array.isArray(filtersState.status.value) &&
          filtersState.status.value.length > 0;
        setSelectedFilters((prev) =>
          prev.filter(
            (item) =>
              item?.toLocaleLowerCase()?.includes('date') ||
              (item?.toLocaleLowerCase()?.includes('status') && hasStatusFilter)
          )
        );
      } else if (updateStatusFilter && !updateDueDateFilter) {
        setSelectedFilters((prev) =>
          prev.filter((item) => item?.toLocaleLowerCase()?.includes('status'))
        );
      }
    }
  }, [selectedFilters, updateStatusFilter, updateDueDateFilter, filtersState, prevSelectedFilters]);

  useEffect(() => {
    if (!updateStatusFilter && !updateDueDateFilter) {
      const hasStatusFilter =
        filtersState.status?.value &&
        Array.isArray(filtersState.status.value) &&
        filtersState.status.value.length > 0;
      setSelectedFilters((prev) =>
        prev.filter(
          (item) =>
            (item?.toLocaleLowerCase()?.includes('status') && hasStatusFilter) ||
            (item?.toLocaleLowerCase()?.includes('date') &&
              (dateRangeOption[item] || dateRange[item]?.length > 0)) ||
            (!item?.toLocaleLowerCase()?.includes('status') &&
              !item?.toLocaleLowerCase()?.includes('date'))
        )
      );
    }
  }, [
    updateStatusFilter,
    updateDueDateFilter,
    filtersState,
    dateRangeOption,
    dateRange,
  ]);

  useEffect(() => {
    const nextBadgeCounts: Record<string, number> = {};
    Object.keys(filtersState)
      .filter((k) => k !== 'sort')
      .forEach((filterKey) => {
        const filterValue = filtersState[filterKey]?.value;
        if (!filterValue) return;
        const count =
          typeof filterValue === 'object'
            ? (filterValue as any[])?.length
            : 1;
        if (!count) return;
        const originalKey = filters.find((f) => f.includes(filterKey));
        const badgeKey = originalKey || filterKey;
        nextBadgeCounts[badgeKey] = count;
        nextBadgeCounts[filterKey] = count;
      });
    setBadgeCounts(nextBadgeCounts);

    if (setHasFilters) {
      setHasFilters(Object.keys(filtersState).some((k) => k !== 'sort'));
    }
  }, [JSON.stringify(filtersState), filters, setHasFilters]);

  if (!filters?.length) return null;

  const filtersToShow = visibleFilters || filters;
  const hasMoreFilters = filters.length > filtersToShow.length;

  return (
    <>
      <FilterDropdownStyle />
      <Row justify="center">
        <Col span={24}>
          {filtersCollapsedByDefault && !filtersRowVisible ? (
            <Space
              style={{ marginBottom: filterRowMarginBottom, flexWrap: 'wrap' }}
            >
              <Button
                type="default"
                icon={
                  <FilterOutlined style={{ color: 'var(--color-text-base, #2e2e2e)' }} />
                }
                onClick={() => {
                  onOpenFilterSelection?.();
                  setFiltersRowVisible(true);
                }}
                style={{
                  height: 32,
                  borderRadius: 10,
                  border: '2px solid var(--color-border, #dbeae3)',
                  color: 'var(--color-text-base, #042a2a)',
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {t('main.filters')}
              </Button>
            </Space>
          ) : (
            <>
              {filtersCollapsedByDefault && filtersRowVisible && (
                <Space style={{ marginBottom: 8, flexWrap: 'wrap' }}>
                  <Button
                    type="link"
                    size="small"
                    icon={<UpOutlined style={{ color: 'var(--color-text-base, #2e2e2e)' }} />}
                    onClick={() => setFiltersRowVisible(false)}
                    style={{ padding: 0, fontSize: 12 }}
                  >
                    {t('main.hide-filters')}
                  </Button>
                </Space>
              )}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  flexWrap: 'wrap',
                  width: '100%',
                  marginBottom: filterRowMarginBottom,
                }}
              >
                <Space style={{ flexWrap: 'wrap', flex: '1 1 auto', minWidth: 0 }}>
                  <Typography
                    style={{
                      fontSize: '12px',
                      fontWeight: '400',
                      lineHeight: '12px',
                      textAlign: 'left',
                      color: 'var(--color-text-base, #042a2a)',
                      minWidth: 35,
                    }}
                  >
                    {t('main.filters')}
                  </Typography>
                  {filtersToShow.map((key) => {
                    const keys = key.split('.');
                    const subKey = keys.length > 1 ? keys[1] : keys[0];
                    const filterKey = keys[0];
                    if (subKey.toLowerCase().includes('date')) {
                      return (
                        <Badge
                          key={subKey}
                        style={{ backgroundColor: 'var(--color-primary, #41aaaf)' }}
                        count={renderFilterBadgeCount(
                          dateRangeOption[subKey] || dateRange[subKey]?.length > 0
                            ? 1
                            : 0
                        )}
                      >
                        <FilterSelect
                          {...filterPopupLayoutProps}
                          className={getSelectedFilterClass(keys[0])}
                          width={matchTriggerWidth ? '100%' : undefined}
                          style={
                            matchTriggerWidth
                              ? { width: '100%', minWidth: 0 }
                              : { minWidth: '150px' }
                          }
                          height="45px"
                          classNames={{
                            popup: { root: 'scope-table-filter-dropdown' },
                          }}
                          placeholder={getPlaceholder(subKey)}
                          aria-label={getPlaceholder(subKey)}
                          popupRender={() => renderDateFilter(subKey)}
                          getPopupContainer={matchTriggerWidth ? getPwaPopupContainer : () => document.body}
                          onOpenChange={(open) =>
                            handleOpenDateFilterDropdown(open, subKey)
                          }
                          open={openFilter[subKey]?.value}
                          value={
                            dateRangeOption[subKey]
                              ? getDateOptionLabel(
                                  String(dateRangeOption[subKey]),
                                  t
                                )
                              : undefined
                          }
                        >
                          <Select.Option key={subKey}>{''}</Select.Option>
                        </FilterSelect>
                      </Badge>
                    );
                  }
                  if (isNumberFilterKey(subKey)) {
                    return (
                      <FilterSelect
                        {...filterPopupLayoutProps}
                        key={subKey}
                        className={`${getSelectedFilterClass(
                          keys[0]
                        )} net-amount-filter-select`}
                        height="45px"
                        classNames={{
                          popup: { root: 'scope-table-filter-dropdown' },
                        }}
                        placeholder={getPlaceholder(subKey)}
                        aria-label={getPlaceholder(subKey)}
                        style={
                          matchTriggerWidth
                            ? { width: '100%', minWidth: 0 }
                            : { minWidth: '100px', width: 'auto' }
                        }
                        popupRender={() => renderNetAmountFilter(subKey)}
                        getPopupContainer={matchTriggerWidth ? getPwaPopupContainer : () => document.body}
                        onOpenChange={(open) =>
                          handleOpenNetAmountFilterDropdown(open, subKey)
                        }
                        open={openFilter[subKey]?.value}
                        value={
                          numberFilterByKey[subKey]?.value !== undefined &&
                          numberFilterByKey[subKey]?.value !== null
                            ? `${
                                operators.find(
                                  (op) =>
                                    op.value === numberFilterByKey[subKey]?.operator
                                )?.label || ''
                              } ${numberFilterByKey[subKey]?.value}`
                            : undefined
                        }
                      >
                        <Select.Option key={subKey}>{''}</Select.Option>
                      </FilterSelect>
                    );
                  }
                  if (isTextFilterKey(subKey)) {
                    const textValue = (
                      filtersState[subKey]?.value as string[] | undefined
                    )?.[0];
                    return (
                      <FilterSelect
                        {...filterPopupLayoutProps}
                        key={subKey}
                        className={getSelectedFilterClass(keys[0])}
                        height="45px"
                        classNames={{
                          popup: { root: 'scope-table-filter-dropdown' },
                        }}
                        placeholder={getPlaceholder(subKey)}
                        aria-label={getPlaceholder(subKey)}
                        style={
                          matchTriggerWidth
                            ? { width: '100%', minWidth: 0 }
                            : { minWidth: '150px', width: 'auto' }
                        }
                        suffixIcon={
                          <SearchOutlined
                            style={{
                              color: 'var(--color-text-muted, #9ca7b0)',
                              fontSize: 14,
                            }}
                          />
                        }
                        popupRender={() => renderTextFilter(subKey)}
                        getPopupContainer={matchTriggerWidth ? getPwaPopupContainer : () => document.body}
                        onOpenChange={(open) =>
                          handleOpenTextFilterDropdown(open, subKey)
                        }
                        open={openFilter[subKey]?.value}
                        value={textValue || undefined}
                      >
                        <Select.Option key={subKey}>{''}</Select.Option>
                      </FilterSelect>
                    );
                  }
                  return (
                    <Popover
                      key={filterKey}
                      placement="topRight"
                      arrow={false}
                      trigger="hover"
                      open={
                        hasCommittedFilterValue(keys[0]) &&
                        hovered &&
                        hoveredFilter === filterKey
                      }
                      content={getPopoverContent(key.split('.')[0])}
                      onOpenChange={(open: boolean) => {
                        setHovered(open);
                        setHoveredFilter(filterKey);
                      }}
                      title=""
                    >
                      <Badge
                        style={{ backgroundColor: 'var(--color-primary, #41aaaf)' }}
                        count={renderFilterBadgeCount(
                          getBadgeCount(key.split('.')[0]) ||
                            badgeCounts[key.split('.')[0]] ||
                            0
                        )}
                      >
                        <FilterSelect
                          {...filterPopupLayoutProps}
                          // popupRender wraps the menu. Ant's virtual list then
                          // reuses one row while scrolling, so later boxes all
                          // paint as whichever label was recycled (often Box 3).
                          virtual={false}
                          className={getSelectedFilterClass(keys[0])}
                          width={matchTriggerWidth ? '100%' : undefined}
                          style={
                            matchTriggerWidth
                              ? { width: '100%', minWidth: 0 }
                              : { minWidth: '150px', width: 'auto' }
                          }
                          height="45px"
                          classNames={{
                            popup: { root: 'scope-table-filter-dropdown' },
                          }}
                          options={customFilterOptions?.[keys[0]]}
                          aria-label={getPlaceholder(keys[0], key)}
                          placeholder={getPlaceholder(keys[0], key)}
                          mode="multiple"
                          styles={{ popup: { root: getPopupStyle(key) } }}
                          getPopupContainer={matchTriggerWidth ? getPwaPopupContainer : () => document.body}
                          popupRender={(items) =>
                            renderMultiFilter(items, filterKey, key) || <div />
                          }
                          onOpenChange={(open) =>
                            handleOpenFilterDropdown(open, filterKey)
                          }
                          open={openFilter[filterKey]?.value}
                          onChange={(val: any) => {
                            const stateKey = key.split('.')[0];
                            selectValueRefs.current[stateKey] = val;
                            handleFilterChange(stateKey, val);
                          }}
                          value={(() => {
                            const stateKey = key.split('.')[0];
                            const filterValue = filtersState[stateKey]?.value;
                            if (
                              filterValue &&
                              Array.isArray(filterValue) &&
                              filterValue.length > 0
                            ) {
                              if (USER_NAME_FILTERS.includes(stateKey)) {
                                return filterValue
                                  .map((val: string) =>
                                    typeof val === 'string'
                                      ? val.trim().replace(/\s+/g, ' ')
                                      : val
                                  )
                                  .filter(Boolean);
                              }
                              return filterValue;
                            }
                            return [];
                          })()}
                          showSearch={key.split('.')[0] !== 'accountActivated'}
                          filterOption={(input, option) => {
                            const stateKey = key.split('.')[0];
                            if (stateKey === 'country' && mainLocaleKey !== 'vat-checker') {
                              return option?.title
                                ?.toLowerCase()
                                .includes(input.toLowerCase());
                            }
                            if (
                              stateKey === 'viesStatus' ||
                              (stateKey === 'accountType' && mainLocaleKey === 'users')
                            ) {
                              return option?.key
                                ?.toLowerCase()
                                .includes(input.toLowerCase());
                            }
                            if (stateKey === 'providerName' || stateKey === 'provider') {
                              return getProviderName(option?.value)
                                ?.toLowerCase()
                                .includes(input.toLowerCase());
                            }
                            if (customFilterOptions?.[stateKey]) {
                              return (option?.label ?? option?.title ?? '')
                                .toString()
                                .toLowerCase()
                                .includes(input.toLowerCase());
                            }
                            return option?.value
                              ?.toLowerCase()
                              .includes(input.toLowerCase());
                          }}
                          optionFilterProp={
                            customFilterOptions?.[key.split('.')[0]] ? 'label' : undefined
                          }
                          onSearch={
                            key.split('.')[0] !== 'accountActivated'
                              ? (e) => onSearch(e, key.split('.')[0])
                              : undefined
                          }
                          filterSort={(optionA, optionB) => {
                            const stateKey = key.split('.')[0];
                            if (stateKey === 'country' && mainLocaleKey !== 'vat-checker') {
                              return (optionA?.title?.toLowerCase() || '').localeCompare(
                                optionB?.title?.toLowerCase() || ''
                              );
                            }
                            if (
                              stateKey === 'viesStatus' ||
                              (stateKey === 'accountType' && mainLocaleKey === 'users')
                            ) {
                              return (optionA?.key?.toLowerCase() || '').localeCompare(
                                optionB?.key?.toLowerCase() || ''
                              );
                            }
                            return (optionA?.value?.toLowerCase() || '').localeCompare(
                              optionB?.value?.toLowerCase() || ''
                            );
                          }}
                        >
                          {renderFilterOptions(key)}
                        </FilterSelect>
                      </Badge>
                    </Popover>
                  );
                })}
                {matchTriggerWidth ? (
                  appendedFilterComponent ? (
                    <div
                      className="scope-table-filters-appended"
                      style={{ width: '100%' }}
                    >
                      {appendedFilterComponent}
                    </div>
                  ) : null
                ) : (
                  <>
                    {onOpenFilterSelection && (
                      <ToggleButton
                        key="view-more-filters"
                        type="primary"
                        shape="circle"
                        icon={<PlusOutlined />}
                        aria-label={
                          hasMoreFilters
                            ? t('main.moreFilters')
                            : t('main.editFilters')
                        }
                        onClick={onOpenFilterSelection}
                        style={{ paddingLeft: '72px' }}
                      >
                        <span className="show-on-hover">
                          {hasMoreFilters
                            ? t('main.moreFilters')
                            : t('main.editFilters')}
                        </span>
                      </ToggleButton>
                    )}
                    {appendedFilterComponent && (
                      <div style={{ float: 'right' }}>{appendedFilterComponent}</div>
                    )}
                    <ClearFiltersButton
                      type="link"
                      className="ant-btn css-s3toao ant-btn-link sc-dmyDmy hOJSVV"
                      style={{
                        fontWeight: 600,
                        color: 'var(--color-text-base, #042a2a)',
                      }}
                      onClick={clearFilters}
                    >
                      {t('main.clear-filters')}
                    </ClearFiltersButton>
                  </>
                )}
                </Space>
                {matchTriggerWidth ? (
                  <div
                    className="scope-table-filters-mobile-footer"
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      flexWrap: 'nowrap',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                      gap: 12,
                    }}
                  >
                    <ClearFiltersButton
                      type="link"
                      className="scope-table-filters-mobile-footer__clear"
                      style={{
                        fontWeight: 600,
                        color: 'var(--color-text-base, #042a2a)',
                        width: 'auto',
                        flex: '0 0 auto',
                      }}
                      onClick={clearFilters}
                    >
                      {t('main.clear-filters')}
                    </ClearFiltersButton>
                    <div
                      className="scope-table-filters-mobile-footer__right"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        flex: '0 0 auto',
                        marginLeft: 'auto',
                      }}
                    >
                      {onOpenFilterSelection && (
                        <Button
                          type="default"
                          shape="circle"
                          className="scope-icon-chip"
                          icon={<PlusOutlined />}
                          aria-label={
                            hasMoreFilters
                              ? t('main.moreFilters')
                              : t('main.editFilters')
                          }
                          onClick={onOpenFilterSelection}
                          style={{
                            width: 32,
                            minWidth: 32,
                            height: 32,
                            padding: 0,
                            background: 'transparent',
                            border: '1px solid var(--color-border, #dbeae3)',
                            color: 'var(--color-text-base, #042a2a)',
                            boxShadow: 'none',
                          }}
                        />
                      )}
                      {footerRightContent}
                    </div>
                  </div>
                ) : null}
                {filterTrailingContent ? (
                  <div
                    style={{
                      flex: '0 1 auto',
                      marginLeft: 'auto',
                      minWidth: 0,
                      maxWidth: '100%',
                    }}
                  >
                    {filterTrailingContent}
                  </div>
                ) : null}
              </div>
              {filterExtraContent}
            </>
          )}
        </Col>
      </Row>
    </>
  );
};

export const ScopeTableFilters = React.memo(ScopeTableFiltersInner);
